import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Space, Typography, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import SpreadsheetGrid from "../../../../components/common/SpreadsheetGrid";
import { measurementUnitApi } from "../../../../api/v3/masterDataApi";
import { buildProductGridColumns } from "./productGridConfig";

const { Text } = Typography;

const REQUIRED_FIELDS = {
    product_code: "产品代码",
    name: "产品名称",
    cost_type: "费用类型",
    unit: "单位",
    recognition_hints: "识别提示",
};

const GRID_ID_FIELD = "_grid_id";

const normalizeText = (value) => String(value ?? "").trim();

const createClientIdFactory = () => {
    let nextId = 1;
    return () => `product-row-${Date.now()}-${nextId++}`;
};

const createNextProductCode = (products) => {
    const existingCodes = new Set(products.map((product) => product.product_code).filter(Boolean));
    let index = products.length + 1;
    let code = `new_product_${index}`;
    while (existingCodes.has(code)) {
        index += 1;
        code = `new_product_${index}`;
    }
    return code;
};

const stripGridMeta = (row) => {
    const airport = normalizeText(row.airport_iata_code).toUpperCase();
    const product = {
        product_code: normalizeText(row.product_code),
        name: normalizeText(row.name),
        cost_type: row.cost_type || undefined,
        unit: row.unit || "个",
        recognition_hints: normalizeText(row.recognition_hints),
    };
    if (airport) product.airport_iata_code = airport;
    return product;
};

const buildDuplicateCodeSet = (products) => {
    const counts = new Map();
    products.forEach((product) => {
        const code = normalizeText(product.product_code);
        if (!code) return;
        counts.set(code, (counts.get(code) || 0) + 1);
    });
    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([code]) => code));
};

/**
 * 产品列表编辑器。
 * 用于编辑 KNOWLEDGE 类型的 json_content.products 数据，外层表单负责最终提交。
 */
const ProductListEditor = ({ value = { products: [] }, onChange, readOnly = false }) => {
    const [unitOptions, setUnitOptions] = useState([]);
    const [unitsLoading, setUnitsLoading] = useState(false);
    const rowIdsRef = useRef([]);
    const createClientIdRef = useRef(createClientIdFactory());

    const products = value?.products || [];

    useEffect(() => {
        let ignore = false;
        const loadUnits = async () => {
            setUnitsLoading(true);
            try {
                const response = await measurementUnitApi.listUnits({ page: 1, page_size: 500, is_active: true });
                if (ignore) return;
                const options = (response.data?.items || []).map((unit) => ({
                    value: unit.code,
                    label: unit.display_name && unit.display_name !== unit.code ? `${unit.display_name} (${unit.code})` : unit.code,
                    displayName: unit.display_name,
                }));
                setUnitOptions(options);
            } catch (error) {
                if (!ignore) message.error("加载单位列表失败");
            } finally {
                if (!ignore) setUnitsLoading(false);
            }
        };

        loadUnits();
        return () => {
            ignore = true;
        };
    }, []);

    while (rowIdsRef.current.length < products.length) {
        rowIdsRef.current.push(createClientIdRef.current());
    }
    if (rowIdsRef.current.length > products.length) {
        rowIdsRef.current = rowIdsRef.current.slice(0, products.length);
    }

    const rows = useMemo(
        () => products.map((product, index) => ({ ...product, [GRID_ID_FIELD]: rowIdsRef.current[index] })),
        [products],
    );

    const duplicateCodes = useMemo(() => buildDuplicateCodeSet(products), [products]);

    const getCellError = useCallback((row, key) => {
        if (REQUIRED_FIELDS[key] && !normalizeText(row[key])) {
            return `请填写${REQUIRED_FIELDS[key]}`;
        }
        if (key === "product_code" && duplicateCodes.has(normalizeText(row.product_code))) {
            return "产品代码重复";
        }
        const airportCode = normalizeText(row.airport_iata_code).toUpperCase();
        if (key === "airport_iata_code" && airportCode && !/^[A-Z]{3}$/.test(airportCode)) {
            return "机场代码需为 3 位 IATA 代码";
        }
        return null;
    }, [duplicateCodes]);

    const invalidCellCount = useMemo(
        () => rows.reduce((count, row) => count + Object.keys(REQUIRED_FIELDS).filter((key) => getCellError(row, key)).length + (getCellError(row, "airport_iata_code") ? 1 : 0), 0),
        [getCellError, rows],
    );

    const columns = useMemo(
        () => buildProductGridColumns({ unitOptions, unitsLoading }),
        [unitOptions, unitsLoading],
    );

    const applyRows = (nextRows) => {
        rowIdsRef.current = nextRows.map((row) => row[GRID_ID_FIELD] || createClientIdRef.current());
        onChange({ products: nextRows.map(stripGridMeta) });
    };

    const handleAddRow = () => {
        const nextRow = {
            [GRID_ID_FIELD]: createClientIdRef.current(),
            product_code: createNextProductCode(products),
            name: "",
            cost_type: "GROUND_HANDLING",
            unit: "个",
            recognition_hints: "",
            airport_iata_code: "",
        };
        applyRows([...rows, nextRow]);
    };

    const handleDeleteRow = (row) => {
        applyRows(rows.filter((item) => item[GRID_ID_FIELD] !== row[GRID_ID_FIELD]));
        message.success("产品已删除");
    };

    return (
        <div className="product-list-editor">
            {!readOnly && (
                <Space align="center" style={{ marginBottom: 12 }}>
                    <Button type="dashed" icon={<PlusOutlined />} onClick={handleAddRow}>
                        添加产品
                    </Button>
                    <Text type="secondary">共 {products.length} 个产品</Text>
                    <Text type="secondary">双击单元格编辑，拖选后可复制到 Excel。</Text>
                </Space>
            )}

            {invalidCellCount > 0 && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={`当前产品列表还有 ${invalidCellCount} 处需要处理`}
                    description="请补全必填字段，确保产品代码不重复。红色单元格悬浮后可查看具体原因。"
                />
            )}

            <SpreadsheetGrid
                rows={rows}
                columns={columns}
                rowKeyGetter={(row) => row[GRID_ID_FIELD]}
                onRowsChange={applyRows}
                readOnly={readOnly}
                getCellError={getCellError}
                onDeleteRow={readOnly ? null : handleDeleteRow}
                emptyDescription="暂无产品数据，点击上方添加产品开始维护。"
                rowHeight={42}
            />
        </div>
    );
};

export default ProductListEditor;
